import React, { useCallback, useEffect, useMemo, useRef, useState, Dispatch, SetStateAction } from 'react';
import { Loader2, Info, TrendingUp, TrendingDown, Star, Table2, Slash, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BaseTooltip } from '@/components';
import { ButtonGroup } from '@/components/uiKit/Button/ButtonGroup';
import { LegendSearchEntry, type VerticalLine, type ExpandableLegendSeries } from '../../types';
import { formatComparisonDate } from '../../utils/formatters';
import { generateTestId } from '@/utils/shared/generateTestIds';
import { ExpandableLegendSeriesPopover } from '../legend/ExpandableLegendSeriesPopover';
import { getRawColorByIndex } from '../../utils/sensorDataColorMap';
import type { YAxisScaleMode } from '../toolbar/YAxisScalePopover';

interface SensorLegendProps {
  title: string;
  sensors: Record<string, boolean>;
  sensorColors: Record<string, string>;
  onToggleSensor: (sensorName: string) => void;
  onAddSensor?: (sensorName: string, withScroll?: boolean) => void;
  onReset: () => void;
  onClearAll: () => void;
  sectionType?: string;
  data?: any;
  isLoading?: boolean;
  syncSectionLegends?: boolean;
  selectedOptionFromAddSensorDropdown?: LegendSearchEntry | null;
  setSelectedOptionFromAddSensorDropdown?: Dispatch<SetStateAction<LegendSearchEntry | null>>;
  focusMode?: boolean;
  focusedSensor?: string | { sensorName: string; sectionId: string } | null;
  onFocus?: (sensorName: string, sectionId?: string) => void;
  onToggleFocusMode?: (sectionId: string) => void; // Toggle focus mode for a specific section
  // Global loading sensors state - synchronized across all sections
  loadingSensors?: Set<string>;
  setLoadingSensor?: (sensorName: string) => void;
  clearLoadingSensor?: (sensorName: string) => void;
  // Table mode props
  markers?: VerticalLine[];
  benchmarkMarkerId?: string | null;
  onSetBenchmark?: (markerId: string) => void;
  showPercentage?: boolean;
  onTogglePercentage?: (show: boolean) => void;
  showTableView?: boolean;
  onToggleTableView?: (show: boolean) => void;
  selectedMarkerLabel?: string | null;
  onSelectMarker?: (markerLabel: string | null) => void;
  onRemoveMarker?: (markerId: string) => void;
  // Expandable Legend Series props
  expandableLegendSeries?: ExpandableLegendSeries[]; // Timestamp rows beneath focused sensors
  expandableLegendFocusMode?: boolean; // Whether focus mode is active for expandable legend (controls checkbox vs radio buttons)
  popoverTimestamps?: any[]; // Flat timestamps for popover (snapshots dropdown)
  onToggleExpandableSeries?: (seriesId: string) => void;
  onToggleExpandableSeriesFocus?: (seriesId: string) => void;
  onToggleExpandableTimestamp?: (seriesId: string, timestampId: string) => void;
  markerLabel?: string; // Dynamic label for markers (e.g., "Timestamps" for sensors, "Markers" for spectrum)
  expandableLegendLabel?: string; // Dynamic label for expandable legend series (e.g., "Timestamps" for spectrum, "Markers" for sensors)
  showInlineFocusButton?: boolean; // Control visibility of inline "Focus" button next to each sensor (default: true)
  showSectionControls?: boolean; //Show or hide the Clear/Reset buttons for this section
  hasCachedDataForSensor?: (sensorName: string) => boolean; // Check if sensor has cached data (to hide "no data" icon)
  // Y-Axis scale mode for spectrum charts
  scaleMode?: YAxisScaleMode;
  customDecimals?: number;
  // Control whether to show focus mode indicator (only relevant for multi-select)
  allowMultiTimestampMode?: boolean;
}

/**
 * Simplified SensorLegend component that eliminates complex state management and timing issues.
 *
 * Key simplifications:
 * - Removed complex loading state coordination
 * - Simple data validation based on actual data presence
 * - No complex debouncing or pending action management
 * - Clear, immediate UI feedback
 */
export const SensorLegend = ({
  title,
  sensors,
  sensorColors,
  onToggleSensor,
  onAddSensor,
  onReset,
  onClearAll,
  sectionType = '',
  data,
  isLoading = false,
  syncSectionLegends,
  selectedOptionFromAddSensorDropdown,
  setSelectedOptionFromAddSensorDropdown,
  focusMode = false,
  focusedSensor = null,
  onFocus,
  onToggleFocusMode,
  loadingSensors,
  setLoadingSensor,
  markers = [],
  benchmarkMarkerId,
  onSetBenchmark,
  showPercentage = true,
  onTogglePercentage,
  showTableView: externalShowTableView,
  onToggleTableView,
  selectedMarkerLabel,
  onSelectMarker,
  onRemoveMarker,
  expandableLegendSeries = [],
  expandableLegendFocusMode = false,
  popoverTimestamps,
  onToggleExpandableSeries,
  onToggleExpandableSeriesFocus,
  onToggleExpandableTimestamp,
  markerLabel = 'Markers',
  expandableLegendLabel,
  showInlineFocusButton = true,
  showSectionControls = true,
  hasCachedDataForSensor,
  scaleMode,
  customDecimals = 2,
  allowMultiTimestampMode = true,
}: SensorLegendProps) => {
  // Click timeout ref for distinguishing single/double clicks
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Toggle between table view (with marker columns) and simple list view - use controlled state if provided
  const [localShowTableView, setLocalShowTableView] = useState(true);
  const showTableView = externalShowTableView !== undefined ? externalShowTableView : localShowTableView;
  const handleToggleTableView = onToggleTableView || setLocalShowTableView;

  // Simple data validation
  const validateSensorData = useCallback((sensorData: any): boolean => {
    if (!sensorData) return false;

    // Check if sensor has valid data points
    if (Array.isArray(sensorData.x) && Array.isArray(sensorData.y)) {
      return sensorData.x.length > 0 && sensorData.y.length > 0;
    }

    // Check for other data indicators
    return !!(sensorData.hasData || sensorData.loaded || sensorData.ready);
  }, []);

  // Get sensor data status
  const getSensorStatus = useCallback(
    (sensorName: string) => {
      if (!sensors[sensorName]) {
        return { enabled: false, loading: false, hasData: false, failed: false };
      }

      const loading = isLoading || loadingSensors?.has(sensorName) || false;

      if (loading) {
        return { enabled: true, loading: true, hasData: false, failed: false };
      }

      // Check if sensor has data (ensure data.data is an array before calling find)
      const sensorData = Array.isArray(data?.data)
        ? data.data.find((s: any) => s.displayName === sensorName)
        : undefined;
      const hasData = validateSensorData(sensorData);
      // If enabled but no data AND no cached data, consider it failed
      const hasCached = hasCachedDataForSensor?.(sensorName) || false;
      const failed = !hasData && !hasCached;

      return { enabled: true, loading: false, hasData, failed };
    },
    [sensors, isLoading, loadingSensors, data, validateSensorData, hasCachedDataForSensor]
  );

  // Handle sensor toggle with immediate UI feedback
  const handleToggleSensor = useCallback(
    (sensorName: string) => {
      const isCurrentlyEnabled = sensors[sensorName];

      // Check if we're disabling the currently focused sensor
      if (isCurrentlyEnabled && focusMode && onFocus) {
        const isFocusedSensor =
          typeof focusedSensor === 'string'
            ? focusedSensor === sensorName
            : focusedSensor?.sensorName === sensorName && focusedSensor?.sectionId === sectionType;

        if (isFocusedSensor) {
          // Exit focus mode before disabling the sensor
          onFocus(sensorName, sectionType);
        }
      }

      if (!isCurrentlyEnabled && setLoadingSensor) {
        // Enabling sensor - show loading immediately across all legends
        setLoadingSensor(sensorName);
      }

      onToggleSensor(sensorName);
    },
    [sensors, onToggleSensor, setLoadingSensor, focusMode, focusedSensor, onFocus, sectionType]
  );

  // Handle add sensor with immediate UI feedback
  const handleAddSensor = useCallback(
    (sensorName: string, withScroll?: boolean) => {
      if (!onAddSensor) return;

      // Show loading immediately across all legends
      if (setLoadingSensor) {
        setLoadingSensor(sensorName);
      }

      onAddSensor(sensorName, withScroll);
    },
    [onAddSensor, setLoadingSensor]
  );

  // Handle reset
  const handleReset = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onReset();
    },
    [onReset]
  );

  // Handle clear all
  const handleClearAll = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onClearAll();
    },
    [onClearAll]
  );

  // Sort sensors - enabled first, then alphabetically
  const sortedSensors = useMemo(() => {
    return Object.entries(sensors).sort(([nameA, enabledA], [nameB, enabledB]) => {
      if (enabledA === enabledB) {
        return nameA.localeCompare(nameB);
      }
      return enabledB ? 1 : -1;
    });
  }, [sensors]);

  // Create a color map for ALL sensors, ensuring every sensor gets a color
  const allSensorColors = useMemo(() => {
    const colorMap: Record<string, string> = {};
    let colorIndex = 0;

    // Go through ALL sensors in order and assign colors
    sortedSensors.forEach(([sensorName]) => {
      // First check if this sensor already has a color assigned
      if (sensorColors[sensorName] && sensorColors[sensorName] !== '#ccc') {
        colorMap[sensorName] = sensorColors[sensorName];
      } else {
        // Assign a color from the palette, cycling through if needed
        const newColor = getRawColorByIndex(colorIndex);
        colorMap[sensorName] = newColor;
        colorIndex++;
      }
    });

    return colorMap;
  }, [sortedSensors, sensorColors]);

  // Get visible markers (max 4) - CRITICAL: Sort by timestamp before slicing
  const visibleMarkers = useMemo(() => {
    if (!markers || markers.length === 0) {
      return [];
    }

    // Sort markers by timestamp (chronological order)
    const sorted = [...markers].sort((a, b) => {
      const aTime = typeof a.timestamp === 'number' ? a.timestamp : a.timestamp.getTime();
      const bTime = typeof b.timestamp === 'number' ? b.timestamp : b.timestamp.getTime();
      return aTime - bTime;
    });

    const result = sorted.slice(0, 4);
    return result;
  }, [markers]);

  // Find benchmark marker
  const benchmarkMarker = useMemo(() => {
    return visibleMarkers.find((m) => m.id === benchmarkMarkerId);
  }, [visibleMarkers, benchmarkMarkerId]);

  // Format value for display based on scale mode
  const formatValue = useCallback(
    (value: number | null): string => {
      if (value === null) return '-';

      // Default formatting for non-spectrum charts (when scaleMode is undefined)
      if (!scaleMode) {
        return value.toFixed(2);
      }

      // Default mode - 2 decimal places
      if (scaleMode === 'default') {
        return value.toFixed(2);
      }

      // Raw mode - show original value as JavaScript represents it
      if (scaleMode === 'raw') {
        return String(value);
      }

      // Custom decimals mode
      if (scaleMode === 'custom') {
        return value.toFixed(customDecimals);
      }

      if (scaleMode === 'log') {
        return value.toExponential(2);
      } else if (scaleMode === 'micro' || scaleMode === 'milli' || scaleMode === 'nano') {
        const scaleFactors: Record<string, { factor: number; symbol: string }> = {
          micro: { factor: 1e6, symbol: 'μ' },
          milli: { factor: 1e3, symbol: 'm' },
          nano: { factor: 1e9, symbol: 'n' },
        };
        const scale = scaleFactors[scaleMode];
        if (scale) {
          return `${(value * scale.factor).toFixed(2)} ${scale.symbol}`;
        }
      }

      // Auto mode - smart formatting based on value magnitude
      const absValue = Math.abs(value);
      if (absValue >= 1) {
        return value.toFixed(1);
      }
      if (absValue >= 0.01) {
        return value.toFixed(2);
      }
      if (absValue >= 0.001) {
        return value.toFixed(3);
      }
      return value.toExponential(2);
    },
    [scaleMode, customDecimals]
  );

  useEffect(() => {
    const sensorName = selectedOptionFromAddSensorDropdown?.[0];
    if (
      sensorName &&
      setSelectedOptionFromAddSensorDropdown &&
      sensorName in sensors &&
      sectionType === selectedOptionFromAddSensorDropdown[1].sectionId
    ) {
      handleAddSensor(sensorName, true);
      setSelectedOptionFromAddSensorDropdown(null);
    }
  }, [selectedOptionFromAddSensorDropdown]);

  // Escape key handler to exit focus mode
  useEffect(() => {
    if (!focusMode || !onFocus) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && focusedSensor) {
        const sensorName = typeof focusedSensor === 'string' ? focusedSensor : focusedSensor.sensorName;
        onFocus(sensorName, sectionType);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [focusMode, focusedSensor, onFocus, sectionType]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      className="space-y-2 max-h-[300px] overflow-x-auto scrollbar-thin group"
      data-testid={`graph_legend_${generateTestId(sectionType)}`}
    >
      {title && (
        <div className="flex items-center gap-2 mb-3">
          <div className="w-3 h-3 bg-[#6667dd] rounded-full"></div>
          <span className="text-sm font-medium text-[#01152b] dark:text-[#e1e8ed]">{title}</span>
        </div>
      )}

      {/* Always render as table for seamless transition */}
      <div>
        <table className="border-collapse w-auto" style={{ tableLayout: 'fixed' }}>
          <thead>
            <tr className="">
              <th className="text-left py-1 pl-1 pr-2 text-xs font-medium text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 w-[280px] min-w-[280px] max-w-[280px] rounded-t-lg sticky top-0 left-0 z-20">
                {/* Expandable Legend Series Popover - timestamps dropdown */}
                {/* Hide when in focus mode - timestamps shown under focused sensor instead */}
                {popoverTimestamps && !focusMode && (
                  <div className="mb-2">
                    <ExpandableLegendSeriesPopover
                      series={popoverTimestamps}
                      focusMode={expandableLegendFocusMode}
                      onToggleSelection={onToggleExpandableSeries!}
                      onToggleFocus={onToggleExpandableSeriesFocus!}
                      onToggleTimestamp={onToggleExpandableTimestamp!}
                      onDeleteSnapshot={onRemoveMarker}
                      markerLabel={expandableLegendLabel || markerLabel}
                    />
                  </div>
                )}

                {/* Focus mode message badge with Unfocus button - only show when multi-timestamp mode is enabled */}
                {focusMode && allowMultiTimestampMode && (
                  <div className="mb-2 px-3 py-1.5 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-700 rounded-lg flex items-center justify-between">
                    <span className="text-sm text-primary-700 dark:text-primary-300 font-medium">Focus Mode</span>
                    {onFocus && focusedSensor && (
                      <button
                        onClick={() => {
                          const sensorName =
                            typeof focusedSensor === 'string' ? focusedSensor : focusedSensor.sensorName;
                          onFocus(sensorName, sectionType);
                        }}
                        className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200 font-medium hover:underline cursor-pointer"
                      >
                        Unfocus
                      </button>
                    )}
                  </div>
                )}

                {/* Control buttons in table header */}
                <div className="flex items-center justify-between">
                  {showSectionControls && (
                    <div className="flex items-center gap-2">
                      <BaseTooltip content={`Clear all sensors in this section only${title ? ` (${title})` : ''}`}>
                        <Button
                          onClick={handleClearAll}
                          variant="ghost"
                          size="sm"
                          className="text-primary-500 font-semibold p-0 flex
                            hover:bg-transparent active:bg-transparent active:text-primary-300
                            transition-colors duration-200 ease-in-out cursor-pointer"
                          type="button"
                          disabled={isLoading}
                          data-testid={`graph_legend_clear_button_${generateTestId(sectionType)}`}
                        >
                          Clear
                        </Button>
                      </BaseTooltip>

                      <span className="text-gray-400">|</span>

                      <BaseTooltip content={`Reset sensors in this section only${title ? ` (${title})` : ''}`}>
                        <Button
                          onClick={handleReset}
                          variant="ghost"
                          size="sm"
                          className="text-primary-500 font-semibold p-0 flex
                            hover:bg-transparent active:bg-transparent active:text-primary-300
                            transition-colors duration-200 ease-in-out cursor-pointer"
                          type="button"
                          disabled={isLoading}
                          data-testid={`graph_legend_reset_button_${generateTestId(sectionType)}`}
                        >
                          Reset
                        </Button>
                      </BaseTooltip>
                    </div>
                  )}

                  {/* Table/List view toggle - only show when there are markers */}
                  {visibleMarkers.length > 0 && (
                    <div className="flex items-center gap-2">
                      <BaseTooltip content={showTableView ? 'Hide comparison table' : 'Show comparison table'}>
                        <Button
                          onClick={() => handleToggleTableView(!showTableView)}
                          variant="ghost"
                          size="sm"
                          className="p-0 flex hover:bg-transparent active:bg-transparent active:text-primary-300 transition-colors duration-200 ease-in-out cursor-pointer relative"
                          type="button"
                          disabled={isLoading}
                        >
                          {showTableView ? (
                            <div className="relative">
                              <Table2 className="w-4 h-4 text-primary-500" />
                              <Slash className="w-4 h-4 text-primary-500 absolute top-0 left-0" />
                            </div>
                          ) : (
                            <Table2 className="w-4 h-4 text-primary-500" />
                          )}
                        </Button>
                      </BaseTooltip>

                      {/* Abs/% Toggle - only show when table view is enabled */}
                      {showTableView && !!onTogglePercentage && (
                        <>
                          <span className="text-gray-400">|</span>
                          <BaseTooltip content="Show absolute values relative to benchmark">
                            <Button
                              onClick={() => {
                                onTogglePercentage?.(false);
                              }}
                              variant="ghost"
                              size="sm"
                              className={`font-semibold p-0 flex
                                  hover:bg-transparent active:bg-transparent
                                  transition-colors duration-200 ease-in-out cursor-pointer ${
                                    !showPercentage
                                      ? 'text-primary-600 dark:text-primary-400 font-bold'
                                      : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400'
                                  }`}
                              type="button"
                              disabled={isLoading}
                            >
                              Abs
                            </Button>
                          </BaseTooltip>
                          <span className="text-gray-400">|</span>
                          <BaseTooltip content="Show percentage change relative to benchmark">
                            <Button
                              onClick={() => {
                                onTogglePercentage?.(true);
                              }}
                              variant="ghost"
                              size="sm"
                              className={`font-semibold p-0 flex
                                  hover:bg-transparent active:bg-transparent
                                  transition-colors duration-200 ease-in-out cursor-pointer ${
                                    showPercentage
                                      ? 'text-primary-600 dark:text-primary-400 font-bold'
                                      : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400'
                                  }`}
                              type="button"
                              disabled={isLoading}
                            >
                              %
                            </Button>
                          </BaseTooltip>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </th>
              {/* Only show marker columns when table view is enabled */}
              {showTableView &&
                visibleMarkers.map((marker, index) => {
                  const isBenchmark = marker.id === benchmarkMarkerId;
                  const isSelected = marker.label === selectedMarkerLabel;
                  const isLast = index === visibleMarkers.length - 1;

                  // Format timestamp for display
                  let timestampDisplay = null;
                  if (typeof marker.timestamp === 'number') {
                    // Frequency/numeric data - just show the number
                    timestampDisplay = marker.timestamp.toFixed(1);
                  } else if (marker.timestamp instanceof Date) {
                    // Time-series data - format as date/time
                    const formatted = formatComparisonDate(marker.timestamp);
                    timestampDisplay = `${formatted.date}, ${formatted.time}`;
                  }

                  return (
                    <th
                      key={marker.id}
                      onClick={() => {
                        onSetBenchmark?.(marker.id);
                      }}
                      className={`text-center p-1.5 w-[100px] min-w-[100px] transition-all rounded-t-lg top-0 z-10 group/marker relative ${
                        isBenchmark
                          ? 'bg-gray-100 dark:bg-gray-800 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700'
                          : 'bg-white dark:bg-gray-800 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      {/* Remove marker button - shows on hover */}
                      {onRemoveMarker && (
                        <BaseTooltip content="Remove marker">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onRemoveMarker(marker.id);
                            }}
                            className="absolute top-0.5 right-0.5 opacity-0 group-hover/marker:opacity-100 transition-opacity hover:bg-red-50 dark:hover:bg-red-900/20 rounded p-0.5"
                          >
                            <X className="w-3 h-3 text-gray-400 hover:text-red-500 dark:hover:text-red-400" />
                          </button>
                        </BaseTooltip>
                      )}

                      <div className="flex flex-col items-center justify-center gap-0.5">
                        <BaseTooltip content={isBenchmark ? 'Benchmark' : 'Set as benchmark'}>
                          <div className="text-[10px] whitespace-nowrap font-bold text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors flex items-center gap-1">
                            {marker.label}
                            {isBenchmark && <Star className="w-3 h-3 fill-amber-400 text-amber-400" />}
                          </div>
                        </BaseTooltip>
                        {timestampDisplay && (
                          <div className="text-[10px] text-gray-500 dark:text-gray-500">{timestampDisplay}</div>
                        )}
                      </div>
                    </th>
                  );
                })}
            </tr>
          </thead>

          <tbody>
            {sortedSensors.map(([sensorName, isEnabled], sensorIndex) => {
              const status = getSensorStatus(sensorName);
              const isFocused =
                focusMode &&
                (typeof focusedSensor === 'string'
                  ? focusedSensor === sensorName
                  : focusedSensor?.sensorName === sensorName && focusedSensor?.sectionId === sectionType);
              const isDisabledByFocus = focusMode && focusedSensor !== null && !isFocused;
              const canInteract = !status.loading && !isLoading;

              // Find expandable legend series for this sensor
              const expandableSeries = expandableLegendSeries?.find((series) => series.label === sensorName);
              // Show timestamps if: sensor is focused locally OR the series itself is marked as focused
              // This handles the case where we're in multi-timestamp mode and multiple sensors have timestamps
              const hasTimestamps = (isFocused || expandableSeries?.isFocused) && expandableSeries && expandableSeries.timestamps.length > 0;
              const sensorBaseColor = allSensorColors[sensorName];

              // Use combined focus state for styling (local focus OR series marked as focused)
              const isRowFocused = isFocused || expandableSeries?.isFocused;

              return (
                <React.Fragment key={sensorName}>
                  <tr
                    className={`group/row border-b border-gray-100 dark:border-gray-700 ${
                      isRowFocused
                        ? 'bg-primary-50 hover:bg-primary-100 dark:bg-primary-900/20'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                    } ${isDisabledByFocus && !expandableSeries?.isFocused ? 'opacity-60' : ''}`}
                  >
                    {/* First Column - Sensor Name */}
                    <td
                      className={`pl-1 pr-2 py-1.5 cursor-pointer border-r border-gray-100 dark:border-gray-700 w-[280px] min-w-[280px] max-w-[280px] sticky left-0 z-10 ${
                        isRowFocused ? 'bg-primary-50 dark:bg-primary-900/20' : 'bg-white dark:bg-gray-800'
                      }`}
                      onClick={() => {
                        if (!canInteract) return;

                        if (clickTimeoutRef.current) {
                          clearTimeout(clickTimeoutRef.current);
                        }

                        clickTimeoutRef.current = setTimeout(() => {
                          if (focusMode && onFocus && status.enabled) {
                            onFocus(sensorName, sectionType);
                            return;
                          }

                          if (status.enabled) {
                            handleToggleSensor(sensorName);
                          } else if (onAddSensor) {
                            handleAddSensor(sensorName);
                          } else {
                            handleToggleSensor(sensorName);
                          }
                        }, 250);
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {/* Color indicator */}
                          <div
                            className={`min-w-[4px] max-w-[5px] h-[14px] rounded-sm ${
                              status.enabled ? '' : 'opacity-30'
                            } ${status.loading ? 'animate-pulse' : ''}`}
                            style={{ backgroundColor: allSensorColors[sensorName] || getRawColorByIndex(sensorIndex) }}
                          />

                          {/* Sensor name */}
                          <span
                            data-testid={`sensor_name_${generateTestId(sensorName)}`}
                            className={`text-xs select-none ${
                              status.enabled
                                ? 'text-gray-900 dark:text-[#e1e8ed] font-medium'
                                : 'text-gray-500 dark:text-[#8b98a5]'
                            }`}
                          >
                            {sensorName}
                          </span>
                        </div>

                        {/* Status and focus indicators */}
                        <div className="flex items-center gap-1">
                          {/* Loading/Error indicators */}
                          {status.loading ? (
                            <Loader2 className="w-4 h-4 animate-spin text-primary-600" />
                          ) : status.enabled && status.failed ? (
                            <BaseTooltip content="No Data Available for this selection">
                              <Info className="w-4 h-4 text-amber-500" />
                            </BaseTooltip>
                          ) : null}

                          {/* Focus button - only visible on hover or when focused */}
                          {!status.loading && onFocus && showInlineFocusButton && (
                            <BaseTooltip
                              content={
                                isFocused
                                  ? 'Exit focus mode'
                                  : status.enabled
                                  ? 'Focus this sensor (dim others)'
                                  : 'Enable and focus this sensor (dim others)'
                              }
                            >
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();

                                  if (!status.enabled) {
                                    if (onAddSensor) {
                                      onAddSensor(sensorName);
                                    } else {
                                      handleToggleSensor(sensorName);
                                    }
                                  }

                                  onFocus(sensorName, sectionType);
                                }}
                                className={`px-2 py-0.5 rounded-full text-[9px] font-medium transition-all border ${
                                  isFocused
                                    ? 'text-white bg-primary-600 border-primary-600 shadow-sm'
                                    : 'opacity-0 group-hover/row:opacity-100 text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:border-primary-500 hover:text-primary-600 hover:shadow-sm'
                                }`}
                              >
                                Focus
                              </button>
                            </BaseTooltip>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Timestamp columns - only show when table view is enabled */}
                    {showTableView &&
                      visibleMarkers.map((marker) => {
                        const value = marker.values?.[sensorName] ?? null;
                        const benchmarkValue = benchmarkMarker?.values?.[sensorName] ?? null;
                        const isBenchmark = marker.id === benchmarkMarkerId;

                        // Calculate percentage difference and absolute difference
                        let percentDiff = 0;
                        let absDiff = 0;
                        if (benchmarkValue !== null && value !== null && !isBenchmark) {
                          absDiff = value - benchmarkValue;
                          percentDiff = benchmarkValue !== 0 ? ((value - benchmarkValue) / benchmarkValue) * 100 : 0;
                        }

                        return (
                          <td
                            key={marker.id}
                            className={`text-center px-2 py-1.5 ${isBenchmark ? 'bg-gray-100 dark:bg-gray-800' : ''} ${
                              !status.enabled ? 'opacity-40' : ''
                            }`}
                          >
                            <div className="font-mono text-xs leading-tight">
                              {status.loading ? (
                                <Loader2 className="w-3 h-3 animate-spin mx-auto text-blue-600" />
                              ) : value === null ? (
                                <div className="text-gray-400 dark:text-gray-500">-</div>
                              ) : !isBenchmark && benchmarkValue !== null ? (
                                // Show absolute value + difference (either abs or %)
                                <>
                                  <div>{formatValue(value)}</div>
                                  <div
                                    className={`text-[10px] flex items-center justify-center gap-0.5 ${
                                      (showPercentage ? percentDiff : absDiff) > 0
                                        ? 'text-green-600 dark:text-green-400'
                                        : (showPercentage ? percentDiff : absDiff) < 0
                                        ? 'text-red-600 dark:text-red-400'
                                        : 'text-gray-500'
                                    }`}
                                  >
                                    {(showPercentage ? percentDiff : absDiff) > 0 ? (
                                      <TrendingUp className="w-3 h-3" />
                                    ) : (showPercentage ? percentDiff : absDiff) < 0 ? (
                                      <TrendingDown className="w-3 h-3" />
                                    ) : null}
                                    {showPercentage ? (
                                      <>{Math.abs(percentDiff).toFixed(1)}%</>
                                    ) : (
                                      <>{formatValue(Math.abs(absDiff))}</>
                                    )}
                                  </div>
                                </>
                              ) : (
                                // Show absolute value only (benchmark column)
                                <div>{formatValue(value)}</div>
                              )}
                            </div>
                          </td>
                        );
                      })}
                  </tr>

                  {/* Timestamp rows - only show enabled timestamps */}
                  {hasTimestamps &&
                    expandableSeries!.timestamps
                      .map((timestamp, originalIndex) => ({ timestamp, originalIndex }))
                      .filter(({ timestamp }) => timestamp.enabled)
                      .map(({ timestamp }) => {
                        // Use marker color from vertical line
                        const timestampColor = timestamp.color || sensorBaseColor;
                        // Check if this timestamp is loading (individual or series-level loading)
                        const isTimestampLoading = timestamp.loading || expandableSeries!.timestampsLoading;

                        return (
                          <tr
                            key={`${sensorName}-${timestamp.id}`}
                            className={`border-b border-gray-100 dark:border-gray-700 bg-primary-25 dark:bg-primary-900/10 cursor-pointer group/timestamp ${
                              isTimestampLoading ? 'opacity-70' : ''
                            }`}
                            onClick={() => {
                              if (isTimestampLoading) return;
                              // For Latest, use '__latest__'; for user timestamps, use ISO string
                              const idToToggle = timestamp.id === '__latest__' ? '__latest__' : timestamp.timestamp;
                              onToggleExpandableTimestamp?.(expandableSeries!.id, idToToggle);
                            }}
                          >
                            {/* First Column - Timestamp without checkbox */}
                            <td className="pl-8 pr-2 py-1.5 border-r border-gray-100 dark:border-gray-700 w-[280px] min-w-[280px] max-w-[280px] sticky left-0 z-10 bg-primary-25 dark:bg-primary-900/10">
                              <div className="flex items-center gap-2">
                                {/* Color indicator - variant of sensor color, dimmed when not visible */}
                                <div
                                  className={`min-w-[4px] max-w-[5px] h-[14px] rounded-sm ${
                                    timestamp.visible ? '' : 'opacity-30'
                                  } ${isTimestampLoading ? 'animate-pulse' : ''}`}
                                  style={{ backgroundColor: timestampColor }}
                                />

                                {/* Timestamp label - styled like legend items */}
                                <span
                                  className={`text-xs select-none ${
                                    timestamp.visible
                                      ? 'text-gray-900 dark:text-[#e1e8ed] font-medium'
                                      : 'text-gray-500 dark:text-[#8b98a5]'
                                  }`}
                                >
                                  {timestamp.label}
                                </span>

                                {/* Loading spinner for timestamp */}
                                {isTimestampLoading && (
                                  <Loader2 className="w-3 h-3 animate-spin text-primary-500 ml-auto" />
                                )}
                              </div>
                            </td>

                            {/* Timestamp columns - show empty cells for consistency */}
                            {showTableView &&
                              visibleMarkers.map((marker) => (
                                <td
                                  key={marker.id}
                                  className={`text-center px-2 py-1.5 ${
                                    marker.id === benchmarkMarkerId ? 'bg-gray-100 dark:bg-gray-800' : ''
                                  } ${!timestamp.visible ? 'opacity-40' : ''}`}
                                >
                                  <div className="text-xs text-gray-400 dark:text-gray-500">-</div>
                                </td>
                              ))}
                          </tr>
                        );
                      })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* No sensors message */}
      {Object.keys(sensors).length === 0 && (
        <div className="text-center py-4 text-gray-500 dark:text-[#8b98a5] text-sm">No sensors available</div>
      )}
    </div>
  );
};
